import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  HiMusicalNote,
  HiSparkles,
  HiClock,
  HiRadio
} from 'react-icons/hi2';
import { FaTwitch, FaYoutube } from 'react-icons/fa';

const streamingPlatforms = [
  {
    name: 'Twitch',
    icon: FaTwitch,
    color: '#9146FF',
    gradient: 'from-purple-600 to-purple-700',
    url: 'https://twitch.tv/whiterabbitbangers',
    description: 'Watch live and chat with the community',
  },
  {
    name: 'YouTube',
    icon: FaYoutube,
    color: '#FF0000',
    gradient: 'from-red-600 to-red-700',
    url: 'https://youtube.com/@whiterabbitbangers',
    description: '24/7 lo-fi radio - always on',
  },
];

const features = [
  {
    icon: HiRadio,
    title: '24/7 Streaming',
    description: 'Non-stop lo-fi beats around the clock',
  },
  {
    icon: HiMusicalNote,
    title: 'AI-Enhanced',
    description: 'Classic lo-fi with a touch of AI magic',
  },
  {
    icon: HiSparkles,
    title: 'Unique Flavor',
    description: 'Not your average study beats',
  },
  {
    icon: HiClock,
    title: 'Always Live',
    description: 'Perfect for work, study, or just vibing',
  },
];

export default function LoFi() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="lofi" className="relative py-24 md:py-32 bg-cyber-darker overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-cyber-dark/50 via-cyber-darker to-cyber-dark/50" />

      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4" ref={ref}>
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span className="text-xs font-rajdhani text-white/60 uppercase tracking-wider">Live 24/7</span>
          </div>

          <h2 className="font-orbitron text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">LO-FI </span>
            <span className="bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-pink bg-clip-text text-transparent">
              RADIO
            </span>
          </h2>

          <p className="text-white/60 font-rajdhani text-lg md:text-xl max-w-3xl mx-auto mb-4">
            Streaming 24/7 on Twitch and YouTube. Classic lo-fi vibes with a little <span className="text-cyber-cyan font-semibold">AI spice</span> on top.
          </p>

          <p className="text-white/40 font-rajdhani text-sm max-w-2xl mx-auto">
            Perfect for studying, working, gaming, or just vibing. Tune in anytime, anywhere.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
          {streamingPlatforms.map((platform, index) => (
            <motion.a
              key={platform.name}
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group glass-card rounded-2xl p-8 hover:scale-[1.02] transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              whileHover={{ y: -4 }}
            >
              <div className="flex flex-col items-center text-center">
                <div
                  className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${platform.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                  style={{ boxShadow: `0 0 40px ${platform.color}40` }}
                >
                  <platform.icon className="w-10 h-10 text-white" />
                </div>

                <h3 className="font-orbitron text-2xl font-bold text-white mb-3">
                  {platform.name}
                </h3>

                <p className="text-white/60 font-rajdhani mb-6">
                  {platform.description}
                </p>

                <div
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r ${platform.gradient} text-white font-orbitron text-sm font-semibold group-hover:shadow-lg transition-all`}
                  style={{ boxShadow: `0 0 20px ${platform.color}30` }}
                >
                  <span>Watch Live</span>
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="glass-card rounded-xl p-6 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyber-cyan/20 to-cyber-purple/20 flex items-center justify-center text-cyber-cyan mx-auto mb-4">
                <feature.icon className="w-7 h-7" />
              </div>
              <h4 className="font-orbitron font-semibold text-white mb-2">
                {feature.title}
              </h4>
              <p className="text-white/50 text-sm font-rajdhani">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className="text-white/40 font-rajdhani text-sm">
            Join thousands of listeners vibing to AI-enhanced lo-fi beats
          </p>
        </motion.div>
      </div>
    </section>
  );
}

